import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Tooltip } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { fetchAudioStatus } from "../../lib/api/meetings"
import { prefersM4aPlayback, toM4aUrl } from "../../lib/audio-format"
import { resolveSpeakerColor } from "../../lib/speaker-colors"
import { detectIndexBase, resolveSpeakerLabel, speakerLabelDisplayMap } from "../../lib/speaker-mapping"
import type { ISpeakerVM } from "../../lib/speaker-vm"
import { fmtTimestamp } from "../MeetingSummary/helpers"
import { SpeakerChip } from "../SpeakerChip"
import { NeedsNamesPill } from "../SpeakerChip/NeedsNamesPill"
import type { IAudioPlayerRef, IProps } from "./interfaces"
import { legendSpeakerVM, stripBoilerplate } from "./legend"
import { PlayButton } from "./PlayButton"
import classes from "./styles.module.css"
import {
  BAR_COUNT,
  BAR_FLOOR,
  BAR_KEYS,
  barCountForWidth,
  decodeAudio,
  fallbackBarHeights,
  resamplePeaks,
  seekFraction,
} from "./waveform"

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]
/** How often to poll the AAC-transcode readiness while it's preparing (iOS). */
const AUDIO_STATUS_POLL_MS = 1000

/**
 * The canonical recording player for both Lexicanon and Voxcanon. Renders a
 * custom amplitude waveform where each bar is tinted by its owning speaker
 * (the defining feature), a speaker-band strip, a content-aware chapter
 * ribbon, a speaker legend, and transport controls. Decodes audio via the Web
 * Audio API (works offline / same-origin) and degrades to a deterministic
 * placeholder waveform that fills with colour left-to-right as the recording
 * loads. On iOS the AAC transcode is polled (non-blocking) and playback is
 * gated until it's warm, so older meetings play instead of timing out.
 */
export const Player = forwardRef<IAudioPlayerRef, IProps>(function Player(
  {
    src,
    segments,
    speakerColors,
    speakerNames,
    speakers,
    chapters: chaptersFromProps,
    showChaptersWhenEmpty,
    onSpeakerClick,
    onConfirmSuggestion,
    onRejectSuggestion,
    suggestionPending,
    onPlayingChange,
    onProgress,
    onSpeedChange,
    initialSeekSeconds,
    showLegend = true,
  },
  ref,
): React.JSX.Element {
  const { i18n } = useLingui()
  // Keep the latest callback in a ref so the audio-event effect (which mounts
  // once) always calls the current handler without re-subscribing on identity.
  const onPlayingChangeRef = useRef(onPlayingChange)
  onPlayingChangeRef.current = onPlayingChange
  const onProgressRef = useRef(onProgress)
  onProgressRef.current = onProgress
  const onSpeedChangeRef = useRef(onSpeedChange)
  onSpeedChangeRef.current = onSpeedChange
  // On mobile the chapter ribbon is a horizontal scroll strip (width-independent),
  // so we keep every chapter and surface its timestamp; on desktop the ribbon is
  // proportional, so sub-8% slivers get merged. 48em matches the CSS breakpoint.
  const isMobile = useMediaQuery("(max-width: 48em)", false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [audioDuration, setAudioDuration] = useState(0)
  const [decodedDuration, setDecodedDuration] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [playing, setPlaying] = useState(false)
  // Set when playback can't start/decode (e.g. an iOS device that can't play
  // the source). Surfaces a small affordance instead of silently doing nothing.
  const [playbackError, setPlaybackError] = useState(false)
  const [bars, setBars] = useState<number[]>(() => fallbackBarHeights())
  // True while the Web Audio decode is in flight — drives the loading wipe.
  const [decoding, setDecoding] = useState(true)
  // 0..1 download progress of the waveform decode fetch (streamed).
  const [dlProgress, setDlProgress] = useState(0)
  const [speed, setSpeed] = useState<number>(1)
  const barsRowRef = useRef<HTMLButtonElement | null>(null)
  // Content-box width of the waveform row. The bar count is derived from it so
  // a 320px side rail shows the whole recording instead of clipping the tail.
  const [rowWidth, setRowWidth] = useState(0)

  useLayoutEffect(() => {
    const row = barsRowRef.current
    if (!row) return
    const measure = (): void => {
      const next = row.clientWidth
      setRowWidth((prev) => (prev === next ? prev : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [])

  // iOS Safari can't decode WebM/Opus, so we offer an AAC/MP4 variant the
  // backend transcodes + caches on demand. `?format=m4a` hits that path.
  const m4aSrc = useMemo(() => toM4aUrl(src), [src])

  // Set once a chosen WebM source actually fails to load/decode at runtime —
  // forces the AAC/MP4 variant from then on.
  const [forceM4a, setForceM4a] = useState(false)

  // The Player instance is reused as you navigate between meetings, so reset the
  // runtime WebM→AAC fallback when the recording changes. Done during render via
  // a ref compare (React's "adjust state on prop change" pattern) — no effect.
  const lastSrcRef = useRef(src)
  if (lastSrcRef.current !== src) {
    lastSrcRef.current = src
    setForceM4a(false)
  }

  // Decide the initial source. The crux: `canPlayType` LIES about Opus-in-WebM
  // on WebKit, so any Apple engine is forced onto the AAC/MP4 variant outright
  // (see prefersM4aPlayback). The onError fallback below is the final safety net.
  const prefersM4a = useMemo(() => prefersM4aPlayback(), [])

  // Whether this device plays the transcoded AAC/MP4 (iOS, or a WebM that failed
  // at runtime) rather than the WebM directly.
  const usesM4a = forceM4a || prefersM4a

  // The AAC/MP4 transcode is on-demand: for an older meeting it can take many
  // seconds, and a long blocking request would time out iOS Safari's <audio>
  // (this is exactly why older, un-pre-warmed meetings wouldn't play). So we
  // never point <audio> at the transcode until it's warm — instead we poll the
  // non-blocking status route and gate playback on `ready`. `transcodeReady` is
  // immediately true when we serve the WebM (nothing to transcode).
  const [transcodeReady, setTranscodeReady] = useState(false)
  // 0..1 transcode progress reported by the status poll (iOS "prepare" phase).
  const [prepProgress, setPrepProgress] = useState(0)
  // Whether an in-progress transcode was ever observed this cycle, so the "ready"
  // toast fires only after a real wait — not on an instantly-cached file.
  const sawTranscodingRef = useRef(false)

  // Poll readiness whenever we depend on the AAC variant. `ready` → the file is
  // cached and instantly servable; `transcoding` → 0..1 progress, keep polling;
  // `absent` → no playable variant, surface the error. When we serve the WebM
  // there's nothing to prepare, so we're ready at once.
  useEffect(() => {
    setPlaybackError(false)
    if (!usesM4a) {
      setPrepProgress(1)
      setTranscodeReady(true)
      return
    }
    setPrepProgress(0)
    setTranscodeReady(false)
    sawTranscodingRef.current = false
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const ctrl = new AbortController()
    const tick = async (): Promise<void> => {
      try {
        const status = await fetchAudioStatus(src, ctrl.signal)
        if (stopped) return
        if (status.status === "ready") {
          setPrepProgress(1)
          setTranscodeReady(true)
          // Only celebrate if the user actually waited for a transcode (not an
          // instant cache hit), so we don't toast on every meeting open.
          if (sawTranscodingRef.current) {
            sawTranscodingRef.current = false
            notifications.show({ color: "success", message: i18n._(msg`Recording is ready to play`) })
          }
          return
        }
        if (status.status === "absent") {
          setPlaybackError(true)
          return
        }
        sawTranscodingRef.current = true
        // Transcode progress only ever moves forward. Across replicas a poll can
        // land on a pod whose view lags (the transcoding pod reports live ffmpeg
        // progress; others read the shared lock file, heartbeated every ~5s), so
        // raw values can arrive out of order. Clamp to the max seen so the ring
        // never jumps backward.
        setPrepProgress((prev) => Math.max(prev, status.progress))
      } catch {
        if (stopped) return
        // Transient (aborted / network blip) — fall through to reschedule.
      }
      timer = setTimeout(() => void tick(), AUDIO_STATUS_POLL_MS)
    }
    void tick()
    return () => {
      stopped = true
      ctrl.abort()
      if (timer) clearTimeout(timer)
    }
  }, [usesM4a, src, i18n])

  // True once the device can actually start playback (WebM is always ready; the
  // AAC variant only once warm). Gates the <audio> src, the play controls, and
  // the waveform decode so nothing fires the slow blocking request prematurely.
  const canPlay = !usesM4a || transcodeReady
  // The src the <audio> element loads — held back (undefined) until the AAC is
  // warm so iOS never hangs on (and times out during) a cold transcode.
  const audioSrc = usesM4a ? (transcodeReady ? m4aSrc : undefined) : src

  // A load/decode failure on the WebM source falls back to the AAC/MP4 variant
  // once before surfacing the error — covers browsers that mis-report support.
  const handleAudioError = useCallback((): void => {
    if (!forceM4a && !prefersM4a) {
      setForceM4a(true)
      return
    }
    setPlaybackError(true)
  }, [forceM4a, prefersM4a])

  // Decode the *playable* source for amplitude bins AND a playback duration.
  // Chrome/Firefox decode WebM/Opus natively; Safari/iOS can't, so we decode the
  // AAC/MP4 variant there (CoreAudio handles it). On iOS we wait until the
  // transcode is warm so the decode fetch streams from cache and its download
  // progress is meaningful. The waveform/bands/chapter layout no longer depends
  // on this value — see `visualDuration` — so a decode quirk can't distort the
  // strip width.
  const decodeSrc = prefersM4a ? m4aSrc : src
  useEffect(() => {
    // On iOS the decode source is the AAC variant — wait for the warm transcode.
    if (prefersM4a && !transcodeReady) return
    let cancelled = false
    setBars(fallbackBarHeights())
    setDecodedDuration(null)
    setDecoding(true)
    setDlProgress(0)
    decodeAudio(decodeSrc, (p) => {
      if (!cancelled) setDlProgress(p)
    }).then((res) => {
      if (cancelled) return
      setBars(res.bins)
      if (res.durationSec) setDecodedDuration(res.durationSec)
      setDecoding(false)
    })
    return () => {
      cancelled = true
    }
  }, [decodeSrc, prefersM4a, transcodeReady])

  // Playback duration — drives the transport only: the total-time readout, the
  // play-button enabled state, and the seek clamp. Prefer the decoded buffer
  // length (samples / sampleRate — reliable), then `<audio>.duration` (which can
  // over-report or stay Infinity for our WebM/Opus blobs), then the segment end.
  // NOTE: this is deliberately NOT used to lay out the waveform/bands/chapters —
  // see `visualDuration` below for why.
  const fallbackSegmentDuration = useMemo(() => {
    if (!segments || segments.length === 0) return 0
    return Math.max(...segments.map((s) => s.end))
  }, [segments])
  const duration = decodedDuration ?? (audioDuration > 0 ? audioDuration : fallbackSegmentDuration)

  const windowStopRef = useRef<(() => void) | null>(null)

  useImperativeHandle(
    ref,
    () => ({
      seekTo: (seconds: number) => {
        const a = audioRef.current
        if (!a?.src) return
        a.currentTime = Math.max(0, Math.min(seconds, a.duration || seconds))
        // The "jump to this moment" affordances are play buttons (▶) — start
        // playback from the seeked point rather than just moving the cursor.
        void a.play().catch(() => setPlaybackError(true))
      },
      play: () => {
        const a = audioRef.current
        if (!a?.src) return
        void a.play().catch(() => setPlaybackError(true))
      },
      pause: () => {
        audioRef.current?.pause()
      },
      playWindow: (start: number, end: number) => {
        const a = audioRef.current
        if (!a?.src) return
        windowStopRef.current?.()
        a.currentTime = Math.max(0, Math.min(start, a.duration || start))
        const onTime = (): void => {
          if (a.currentTime >= end) {
            a.pause()
            a.removeEventListener("timeupdate", onTime)
            windowStopRef.current = null
          }
        }
        a.addEventListener("timeupdate", onTime)
        windowStopRef.current = () => a.removeEventListener("timeupdate", onTime)
        void a.play().catch(() => setPlaybackError(true))
      },
      setPlaybackRate: (rate: number) => {
        setSpeed(rate)
      },
    }),
    [],
  )

  const finalSegments = useMemo(() => (segments ? segments.filter((s) => s.isFinal !== false) : []), [segments])

  // Prefer the unified speakers[] label→name map. Fall back to the positional
  // roster + index-base only for meetings that never carried `speakers`.
  const speakerMap = useMemo(
    () => (speakers && speakers.length > 0 ? speakerLabelDisplayMap(speakers) : null),
    [speakers],
  )
  const indexBase = useMemo(() => (speakerMap ? 1 : detectIndexBase(segments)), [segments, speakerMap])
  const resolveOwner = useCallback(
    (rawSpeaker: string): string => {
      if (speakerMap) return resolveSpeakerLabel(rawSpeaker, speakerMap)
      return speakerNames ? resolveSpeakerLabel(rawSpeaker, speakerNames, indexBase) : rawSpeaker
    },
    [speakerMap, speakerNames, indexBase],
  )

  // Visual-layout duration — the content timeline that the speaker bands, the
  // per-bar speaker colours, the chapter ribbon, and the progress cursor are all
  // measured against. It is derived ONLY from the segment + chapter offsets in
  // props, so it is STABLE: it never shifts when the async decode lands.
  //
  // That stability is the fix for the iOS "snap". The playable source on iOS is
  // a transcoded M4A whose decoded length can exceed the segment timeline
  // (transcode priming/padding + trailing silence). Laying the bands out against
  // that playback duration left them ending ~80% short while the flex-filled
  // waveform still spanned the full width — and it visibly *snapped* there the
  // moment decode replaced the segment-end fallback. Positioning every visual
  // row against this content span keeps them mutually aligned and edge-to-edge
  // regardless of the playable container's duration.
  const visualDuration = useMemo(() => {
    let max = 0
    for (const s of finalSegments) max = Math.max(max, s.end)
    if (chaptersFromProps) for (const c of chaptersFromProps) max = Math.max(max, c.end_offset)
    return max
  }, [finalSegments, chaptersFromProps])
  // Use the stable content span when we have segments/chapters; fall back to the
  // playback duration for a bare audio clip (no timeline) so seek still works.
  const layoutDur = visualDuration > 0 ? visualDuration : duration

  // Render-ready bars. Decode stays at BAR_COUNT bins; the row resamples to
  // however many 1px bars actually fit the measured width. A fixed 180 overflows
  // a 320px side rail (only ~159 fit) and clips the end of the recording.
  const displayBarCount = rowWidth > 0 ? barCountForWidth(rowWidth) : BAR_COUNT
  const displayBars = useMemo(() => {
    const haveSegments = layoutDur > 0 && finalSegments.length > 0
    const heights = resamplePeaks(bars, displayBarCount)
    return heights.map((height, i) => {
      let owner: string | null = null
      if (haveSegments) {
        const t = ((i + 0.5) / displayBarCount) * layoutDur
        const seg = finalSegments.find((s) => t >= s.start && t < s.end) ?? finalSegments[finalSegments.length - 1]
        owner = resolveOwner(seg.speaker)
      }
      return { height, owner }
    })
  }, [displayBarCount, bars, layoutDur, finalSegments, resolveOwner])

  // Chapter ribbon — single source of truth: the content-aware chapters from
  // the post-finalize cluster step (`chapters_json`). There is deliberately NO
  // client-side fallback: deriving chapters from progressive summaries created
  // a second, divergent source and masked a broken backend step. When chapters
  // are absent the ribbon simply doesn't render (see `showChaptersWhenEmpty`).
  const chapters = useMemo(() => {
    if (!chaptersFromProps || chaptersFromProps.length === 0 || layoutDur <= 0) return []

    const raw = chaptersFromProps
      .map((c) => ({
        start: Math.max(0, c.start_offset),
        end: Math.min(layoutDur, c.end_offset),
        title: stripBoilerplate(c.title),
        description: c.description ?? "",
      }))
      .filter((c) => c.end > c.start && c.title)
    if (raw.length === 0) return raw

    // Mobile: the ribbon is a scroll strip where chips size to their title, so
    // narrow chapters are fine — keep them all, each individually tappable.
    if (isMobile) return raw

    // Desktop: the ribbon is proportional, so absorb anything below MIN_PCT into
    // its neighbour to avoid illegible slivers. We keep the wider chapter's
    // (already ≤2-sentence) description rather than concatenating — joining
    // descriptions is what produced wall-of-text tooltips.
    const MIN_PCT = 0.08
    const merged: typeof raw = []
    for (const c of raw) {
      const widthPct = (c.end - c.start) / layoutDur
      if (widthPct < MIN_PCT && merged.length > 0) {
        const prev = merged[merged.length - 1]
        const absorbedIsWider = c.end - c.start > prev.end - prev.start
        merged[merged.length - 1] = {
          ...prev,
          end: c.end,
          // Keep the more substantial chapter's title/description; never join.
          title: absorbedIsWider ? c.title : prev.title,
          description: absorbedIsWider ? c.description : prev.description,
        }
      } else {
        merged.push(c)
      }
    }
    return merged
  }, [chaptersFromProps, layoutDur, isMobile])

  // Audio element wiring
  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const handleMeta = (): void => {
      const d = a.duration
      if (Number.isFinite(d) && d > 0) setAudioDuration(d)
    }
    const handleTime = (): void => setCurrentTime(a.currentTime || 0)
    const handlePlay = (): void => {
      setPlaying(true)
      setPlaybackError(false)
      onPlayingChangeRef.current?.(true)
    }
    const handlePause = (): void => {
      setPlaying(false)
      onPlayingChangeRef.current?.(false)
    }
    const handleEnded = (): void => {
      setPlaying(false)
      onPlayingChangeRef.current?.(false)
    }
    a.addEventListener("loadedmetadata", handleMeta)
    a.addEventListener("durationchange", handleMeta)
    a.addEventListener("timeupdate", handleTime)
    a.addEventListener("play", handlePlay)
    a.addEventListener("pause", handlePause)
    a.addEventListener("ended", handleEnded)
    return () => {
      a.removeEventListener("loadedmetadata", handleMeta)
      a.removeEventListener("durationchange", handleMeta)
      a.removeEventListener("timeupdate", handleTime)
      a.removeEventListener("play", handlePlay)
      a.removeEventListener("pause", handlePause)
      a.removeEventListener("ended", handleEnded)
    }
  }, [])

  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    a.playbackRate = speed
    onSpeedChangeRef.current?.(speed)
  }, [speed])

  useEffect(() => {
    onProgressRef.current?.(currentTime, duration)
  }, [currentTime, duration])

  // Deep-link seek: position the playhead at `initialSeekSeconds` once the audio
  // can play. Applied once per value (a ref latch), and via a `canplay` listener
  // so it still lands when the on-demand AAC transcode sets `src` only later.
  const appliedSeekRef = useRef<number | null>(null)
  useEffect(() => {
    if (initialSeekSeconds == null) return
    const a = audioRef.current
    if (!a) return
    if (appliedSeekRef.current === initialSeekSeconds) return

    const apply = (): void => {
      if (!a.src || appliedSeekRef.current === initialSeekSeconds) return
      const d = a.duration
      a.currentTime = Math.max(0, Math.min(initialSeekSeconds, Number.isFinite(d) && d > 0 ? d : initialSeekSeconds))
      appliedSeekRef.current = initialSeekSeconds
    }

    if (a.readyState >= 1) {
      apply()
      return
    }
    a.addEventListener("loadedmetadata", apply)
    a.addEventListener("canplay", apply)
    return () => {
      a.removeEventListener("loadedmetadata", apply)
      a.removeEventListener("canplay", apply)
    }
  }, [initialSeekSeconds])

  const togglePlay = useCallback((): void => {
    const a = audioRef.current
    if (!a) return
    if (a.paused) {
      // No src yet means the AAC transcode is still warming — don't fire a
      // play() that would only reject and surface a spurious error.
      if (!a.src) return
      a.play().catch(() => setPlaybackError(true))
    } else a.pause()
  }, [])

  const seekTo = useCallback((seconds: number): void => {
    const a = audioRef.current
    if (!a) return
    a.currentTime = Math.max(0, Math.min(seconds, a.duration || seconds))
  }, [])

  // The card legend's "listen to this speaker" affordance — seek the shared
  // audio element to the speaker's longest clear segment and start playing.
  // Player owns the <audio>, so listen is an internal seek+play (mirroring the
  // imperative `seekTo` ref) rather than a host round-trip.
  const listenTo = useCallback((seconds: number): void => {
    const a = audioRef.current
    if (!a?.src) return
    a.currentTime = Math.max(0, Math.min(seconds, a.duration || seconds))
    void a.play().catch(() => setPlaybackError(true))
  }, [])

  const handleBarsClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      const row = barsRowRef.current
      if (!row || layoutDur <= 0) return
      const rect = row.getBoundingClientRect()
      seekTo(seekFraction(e.clientX, rect.left, rect.width) * layoutDur)
    },
    [layoutDur, seekTo],
  )

  // Cursor + click-seek run against the same content span the waveform is laid
  // out in, so the playhead tracks the bars exactly. Playback past the content
  // (trailing silence) clamps at 100%.
  const cursorPct = layoutDur > 0 ? Math.max(0, Math.min(100, (currentTime / layoutDur) * 100)) : 0
  const cursorIdx = Math.floor((cursorPct / 100) * displayBarCount)

  // Loading colour-wipe — a single, non-decreasing 0..1 fill that sweeps the
  // bars from grey to colour, left to right. On iOS-cold the transcode "prepare"
  // phase fills the first half and the warm download tops off the second;
  // desktop (no transcode) is just the download. While loading, the wipe
  // wavefront — not the playback cursor — decides which bars are coloured.
  const loading = decoding || !canPlay
  const prepShare = usesM4a ? 0.5 : 0
  const loadProgress = !transcodeReady
    ? prepProgress * prepShare
    : decoding
      ? prepShare + dlProgress * (1 - prepShare)
      : 1
  const wipeIdx = Math.round(loadProgress * displayBarCount)

  // The legend's speaker view-models. When the host passes the unified
  // `speakers` list, render straight from it; otherwise derive the same list the
  // pre-chip legend built — segment owners first, roster names as the no-segments
  // fallback — resolving colour/status through `legendSpeakerVM`.
  const legendSpeakers = useMemo<ISpeakerVM[]>(() => {
    if (speakers && speakers.length > 0) return speakers
    const seen = new Map<string, ISpeakerVM>()
    // Primary: speakers that actually appear in the timed segments.
    for (const seg of finalSegments) {
      const ownerName = resolveOwner(seg.speaker)
      if (seen.has(ownerName)) continue
      seen.set(ownerName, legendSpeakerVM(ownerName, speakerColors))
    }
    // Fallback: meetings without persisted streaming_segments (no per-moment
    // timing) still have a known roster (recognised + diarised). Show the legend
    // from that so speakers + colours appear under the waveform and stay
    // clickable to rename — even though the waveform itself can't be banded.
    if (seen.size === 0) {
      for (const name of speakerNames ?? []) {
        if (!name || seen.has(name)) continue
        seen.set(name, legendSpeakerVM(name, speakerColors))
      }
    }
    return Array.from(seen.values())
  }, [speakers, finalSegments, speakerColors, speakerNames, resolveOwner])

  // A single unresolved speaker flips the whole legend into profile cards led by
  // the "{n} need names" pill (attention-first: suggested → unidentified → the
  // rest, order-stable within each bucket); a fully-resolved legend renders as
  // calm compact pills. `.some`/`.filter` are cheap on a handful of speakers.
  const legendNeedsAttention = legendSpeakers.some((vm) => vm.needsAttention)
  const legendAttentionCount = useMemo(() => legendSpeakers.filter((vm) => vm.needsAttention).length, [legendSpeakers])
  const orderedLegend = useMemo(() => {
    if (!legendNeedsAttention) return legendSpeakers
    const rank = (vm: ISpeakerVM): number => (vm.status === "suggested" ? 0 : vm.status === "unidentified" ? 1 : 2)
    return [...legendSpeakers].sort((a, b) => rank(a) - rank(b))
  }, [legendSpeakers, legendNeedsAttention])

  return (
    <section className={classes.player} data-testid="meeting-summary-player">
      <div className={classes.waveWrap}>
        {/* The framed timeline: the waveform and chapter ribbon share one
            hairline frame so they read as a single scrubber (no card around it
            — the element stands by itself). Rows stay full-bleed inside it,
            keeping their proportional x-positions mutually aligned. The
            transport docks onto the frame's bottom edge as a shelf (same
            language as the report callout's best-model shelf). */}
        <div className={classes.frame} data-testid="player-frame">
          <button
            type="button"
            className={classes.barsRow}
            ref={barsRowRef}
            onClick={handleBarsClick}
            aria-label={i18n._(msg`Seek in recording`)}
            data-loading={loading ? "true" : "false"}
          >
            {displayBars.map((bar, position) => {
              // Each bar's speaker identity colour (or brand fallback when no
              // segment owns it). While loading, the colour-wipe wavefront
              // decides which bars are filled (left = coloured, right = grey);
              // once loaded, the playback cursor does (played vs unplayed).
              const playedColor = bar.owner
                ? resolveSpeakerColor(bar.owner, speakerColors)
                : "var(--ms-waveform-played)"
              const boundaryIdx = loading ? wipeIdx : cursorIdx
              const isFilled = position < boundaryIdx
              // Ahead of the wavefront we render greyer than the normal unplayed
              // mix, so the loading sweep reads clearly as grey → colour.
              const aheadMix = loading ? 12 : 35
              const bg = isFilled
                ? playedColor
                : `color-mix(in oklab, ${playedColor} ${aheadMix}%, var(--ms-waveform-unplayed))`
              // While loading, show a stable low skeleton (a faint, even ripple)
              // rather than the decoded heights — so the real waveform eases up
              // from a neutral placeholder once, instead of morphing from a
              // fake-looking shape into the real one.
              const barHeight = loading
                ? 0.22 + 0.1 * Math.abs(Math.sin(position * 0.6))
                : Math.max(BAR_FLOOR, bar.height)
              return (
                <span
                  key={BAR_KEYS[position]}
                  data-testid="waveform-bar"
                  className={classes.bar}
                  style={{ height: `${barHeight * 100}%`, background: bg }}
                />
              )
            })}
            {layoutDur > 0 && <span className={classes.cursor} style={{ left: `${cursorPct}%` }} />}
          </button>

          {chapters.length > 0 && (
            <div className={classes.chaptersRow}>
              {chapters.map((c, i) => {
                const leftPct = (c.start / layoutDur) * 100
                const widthPct = ((c.end - c.start) / layoutDur) * 100
                const isActive = currentTime >= c.start && currentTime < c.end
                const tooltipLabel = c.description || c.title
                return (
                  <Tooltip
                    key={`${i}-${c.start}`}
                    label={tooltipLabel}
                    multiline
                    w={260}
                    position="top"
                    withArrow
                    openDelay={120}
                    transitionProps={{ duration: 150 }}
                  >
                    <button
                      type="button"
                      className={classes.chapter}
                      data-active={isActive ? "true" : "false"}
                      onClick={() => seekTo(c.start + 0.5)}
                      style={
                        {
                          "--chapter-left": `${leftPct}%`,
                          "--chapter-width": `${widthPct}%`,
                        } as React.CSSProperties
                      }
                    >
                      <span className={classes.chapterBar} />
                      {/* Timestamp prefix — shown only on the mobile scroll strip
                          (CSS), where chips aren't proportional to time. */}
                      <span className={classes.chapterTime}>{fmtTimestamp(c.start)}</span>
                      <span className={classes.chapterTitle}>{c.title}</span>
                    </button>
                  </Tooltip>
                )
              })}
            </div>
          )}
          {showChaptersWhenEmpty && chapters.length === 0 && (
            <div className={classes.chaptersEmpty}>
              <Trans>Chapters unavailable</Trans>
            </div>
          )}
        </div>

        {/* The transport shelf — play, state label, time and speed docked onto
            the frame's bottom edge. */}
        <div className={classes.playShelf}>
          <PlayButton
            size="sm"
            isPlaying={playing}
            disabled={duration <= 0 || !canPlay}
            preparing={usesM4a && !transcodeReady}
            prepProgress={prepProgress}
            onToggle={togglePlay}
            playLabel={i18n._(msg`Play`)}
            pauseLabel={i18n._(msg`Pause`)}
          />
          <span className={classes.shelfLabel}>{playing ? <Trans>Playing</Trans> : <Trans>Play recording</Trans>}</span>
          <span className={classes.shelfTime}>
            {fmtTimestamp(currentTime)} / {fmtTimestamp(duration)}
          </span>
          {/* The multi-second wait on iOS is the transcode warming up; the
              prepare ring on the play button shows progress, this names it. */}
          {usesM4a && !transcodeReady && (
            <span className={classes.preparing}>
              <Trans>Optimizing playback for this device…</Trans>
            </span>
          )}
          <div className={classes.speedGroup}>
            {SPEED_OPTIONS.map((sp) => (
              <button key={sp} type="button" data-active={Math.abs(speed - sp) < 0.01} onClick={() => setSpeed(sp)}>
                {sp}×
              </button>
            ))}
          </div>
        </div>
      </div>

      {showLegend ? (
        <div className={classes.foot}>
          <div className={classes.legendCol}>
            {/* Renders null once everyone is named (attention count 0). */}
            <NeedsNamesPill count={legendAttentionCount} data-testid="player-needs-names" />
            <div className={classes.legend} data-mode={legendNeedsAttention ? "cards" : "compact"}>
              {orderedLegend.map((vm) => {
                const seg = vm.longestSegment
                return (
                  <SpeakerChip
                    key={vm.label ?? vm.displayName}
                    speaker={vm}
                    variant={legendNeedsAttention ? "card" : "compact"}
                    // The chip's edit key is `label ?? displayName`; the internally-
                    // derived legend pins `label` to the resolved owner name, so this
                    // passes exactly what the pre-chip legend passed to onSpeakerClick.
                    onEdit={onSpeakerClick ? (label) => onSpeakerClick(label) : undefined}
                    onConfirmSuggestion={onConfirmSuggestion}
                    onRejectSuggestion={onRejectSuggestion}
                    suggestionPending={suggestionPending}
                    // Listen only when this speaker has a segment to seek to AND the
                    // audio can actually start (WebM always; iOS AAC once warm).
                    onListen={seg != null && canPlay ? () => listenTo(seg.start) : undefined}
                  />
                )
              })}
            </div>
          </div>
        </div>
      ) : null}

      {playbackError && (
        <p className={classes.playbackError} role="alert">
          <Trans>Couldn't play this recording on this device.</Trans>
        </p>
      )}

      {/* `audioSrc` is feature-detected AND gated: WebM/Opus on Chrome/Firefox
          (smaller, no transcode), the on-demand AAC/MP4 variant on iOS Safari —
          and on iOS it's held back (undefined) until the transcode is warm, so
          the element never hangs on (and times out during) a cold transcode.
          onError falls back to the M4A if a WebM that claimed support fails. */}
      <audio ref={audioRef} src={audioSrc} preload="metadata" onError={handleAudioError}>
        <track kind="captions" />
      </audio>
    </section>
  )
})
